import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, X, Shield } from 'lucide-react';
import apiService from '../../services/ApiService';
import { getRoleColor } from '../../utils/roleHelpers';
import { useApp } from '../../context/AppContext';

const ROLES = ['Admin', 'Manager', 'Warehouse', 'Sales', 'Finance'];

const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif';
const BG   = '#F2F2F7';
const INK  = '#1C1C1E';
const MUTED = '#8E8E93';
const CARD = { background: '#fff', borderRadius: 18, boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 4px 20px rgba(0,0,0,0.07)' };
const INP  = { padding: '11px 14px', border: 'none', borderRadius: 10, fontSize: 15, outline: 'none', background: '#F2F2F7', color: '#1C1C1E', fontFamily: FONT, width: '100%', boxSizing: 'border-box' };
const LBL  = { display: 'block', fontSize: 11, fontWeight: 600, color: '#8E8E93', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 };
const SYS_GREEN  = '#34C759';
const SYS_RED    = '#FF3B30';
const SYS_BLUE   = '#007AFF';
const BRAND      = '#84CC16';

const ROLE_COLORS = {
  Admin:     BRAND,
  Manager:   SYS_BLUE,
  Warehouse: '#FF9500',
  Sales:     '#AF52DE',
  Finance:   SYS_GREEN,
};

function UserModal({ user, currentUser, onSave, onClose }) {
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'Warehouse', status: 'active', ...(user || {}) });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isEdit = !!user;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name required.'); return; }
    if (!form.email.trim()) { setError('Email required.'); return; }
    if (!isEdit && form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form };
      if (isEdit && !payload.password) delete payload.password;
      await onSave(payload);
      onClose();
    } catch (err) { setError(err.message || 'Failed to save user.'); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'white', borderRadius: 20, width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.18)', fontFamily: FONT }}>
        {/* Modal header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '22px 24px 18px' }}>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: INK, margin: 0 }}>{isEdit ? 'Edit User' : 'Add User'}</h2>
          <button
            onClick={onClose}
            style={{ width: 30, height: 30, borderRadius: '50%', background: BG, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, flexShrink: 0 }}
          >
            <X size={15} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '0 24px 24px' }}>
          {error && (
            <div style={{ background: '#FFF1F0', borderRadius: 10, padding: '11px 14px', color: SYS_RED, fontSize: 13, marginBottom: 16, fontFamily: FONT }}>
              {error}
            </div>
          )}

          <div style={{ marginBottom: 14 }}>
            <label htmlFor="usr-name" style={LBL}>Full Name *</label>
            <input id="usr-name" type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required style={INP} />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label htmlFor="usr-email" style={LBL}>Email *</label>
            <input id="usr-email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required style={INP} />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label htmlFor="usr-password" style={LBL}>{isEdit ? 'New Password (leave blank to keep)' : 'Password *'}</label>
            <input
              id="usr-password"
              type="password"
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              style={INP}
              placeholder={isEdit ? 'Leave blank to keep current' : 'Min 8 characters'}
              autoComplete="new-password"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
            <div>
              <label htmlFor="usr-role" style={LBL}>Role</label>
              <select id="usr-role" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} style={INP}>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="usr-status" style={LBL}>Status</label>
              <select id="usr-status" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={INP} disabled={isEdit && user?.id === currentUser?.id}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{ padding: '11px 22px', border: 'none', borderRadius: 12, background: BG, color: INK, cursor: 'pointer', fontSize: 15, fontWeight: 500, fontFamily: FONT }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{ padding: '11px 24px', border: 'none', borderRadius: 12, background: BRAND, color: 'white', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 15, fontWeight: 600, opacity: saving ? 0.8 : 1, fontFamily: FONT }}
            >
              {saving ? 'Saving…' : 'Save User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { currentUser } = useApp();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [settings, setSettings] = useState({});
  const [userModal, setUserModal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [u, s, a] = await Promise.all([
        apiService.getUsers().catch(() => []),
        apiService.getSettings().catch(() => ({})),
        apiService.getAudit().catch(() => []),
      ]);
      setUsers(u);
      setSettings(s);
      setAuditLogs(a.slice(0, 100));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  async function handleSaveUser(form) {
    if (userModal === 'new') {
      const created = await apiService.createUser(form);
      setUsers(prev => [...prev, created]);
    } else {
      const updated = await apiService.updateUser(form.id, form);
      setUsers(prev => prev.map(u => u.id === updated.id ? updated : u));
    }
  }

  async function handleDeleteUser(user) {
    if (user.id === currentUser?.id) { alert('You cannot delete your own account.'); return; }
    if (!window.confirm(`Delete user "${user.name}"? This cannot be undone.`)) return;
    await apiService.deleteUser(user.id);
    setUsers(prev => prev.filter(u => u.id !== user.id));
  }

  async function handleSaveSettings(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const updated = await apiService.updateSettings(settings);
      setSettings(updated);
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 3000);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  const TABS = [
    { key: 'users', label: 'User Management' },
    { key: 'app',   label: 'App Settings' },
    { key: 'audit', label: 'Audit Log' },
  ];

  if (loading) return (
    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: BG, fontFamily: FONT, color: MUTED, fontSize: 15 }}>
      Loading settings…
    </div>
  );

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: BG, fontFamily: FONT }}>

      {/* Global error banner */}
      {error && (
        <div style={{ background: '#FFF1F0', color: SYS_RED, padding: '11px 24px', fontSize: 13, fontFamily: FONT, flexShrink: 0 }}>
          {error}
        </div>
      )}

      {/* iOS-style pill segmented tab bar */}
      <div style={{ flexShrink: 0, padding: '16px 24px 0', background: BG }}>
        <div style={{ display: 'inline-flex', background: '#E5E5EA', borderRadius: 12, padding: 3, gap: 2 }}>
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              style={{
                padding: '7px 18px',
                border: 'none',
                borderRadius: 10,
                background: tab === key ? 'white' : 'transparent',
                boxShadow: tab === key ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
                color: tab === key ? INK : MUTED,
                fontWeight: tab === key ? 600 : 500,
                fontSize: 14,
                cursor: 'pointer',
                fontFamily: FONT,
                transition: 'all 0.15s ease',
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Main scrollable content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>

        {/* ── USER MANAGEMENT ── */}
        {tab === 'users' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: INK }}>All Users</span>
              <button
                onClick={() => setUserModal('new')}
                style={{ padding: '9px 18px', background: BRAND, border: 'none', borderRadius: 12, color: 'white', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontFamily: FONT }}
              >
                <Plus size={15} /> Add User
              </button>
            </div>

            <div style={{ ...CARD, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#F7F7F7' }}>
                    {['Name', 'Email', 'Role', 'Status', 'Last Login', 'Actions'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '11px 16px', color: MUTED, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: FONT }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, idx) => {
                    const roleColor = ROLE_COLORS[u.role] || getRoleColor(u.role);
                    const isMe = u.id === currentUser?.id;
                    return (
                      <tr
                        key={u.id}
                        style={{
                          borderTop: idx === 0 ? 'none' : '1px solid rgba(0,0,0,0.05)',
                          background: isMe ? 'rgba(132,204,22,0.06)' : 'white',
                        }}
                      >
                        <td style={{ padding: '14px 16px', fontWeight: 600, color: INK }}>
                          {u.name}
                          {isMe && <span style={{ marginLeft: 6, fontSize: 10, color: BRAND, fontWeight: 700 }}>YOU</span>}
                        </td>
                        <td style={{ padding: '14px 16px', color: MUTED, fontSize: 13 }}>{u.email}</td>
                        <td style={{ padding: '14px 16px' }}>
                          <span style={{
                            padding: '3px 10px',
                            borderRadius: 8,
                            fontSize: 11,
                            fontWeight: 700,
                            background: roleColor + '1A',
                            color: roleColor,
                            fontFamily: FONT,
                            letterSpacing: '0.02em',
                          }}>
                            {u.role}
                          </span>
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          <span style={{
                            padding: '3px 10px',
                            borderRadius: 8,
                            fontSize: 11,
                            fontWeight: 700,
                            background: u.status === 'active' ? SYS_GREEN + '1A' : SYS_RED + '1A',
                            color: u.status === 'active' ? SYS_GREEN : SYS_RED,
                            fontFamily: FONT,
                          }}>
                            {u.status}
                          </span>
                        </td>
                        <td style={{ padding: '14px 16px', color: MUTED, fontSize: 12, fontFamily: FONT }}>
                          {u.lastLogin ? new Date(u.lastLogin).toLocaleString() : 'Never'}
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              onClick={() => setUserModal(u)}
                              style={{ padding: '6px 13px', border: 'none', borderRadius: 8, background: BG, cursor: 'pointer', color: INK, fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4, fontFamily: FONT }}
                            >
                              <Edit2 size={11} /> Edit
                            </button>
                            {!isMe && (
                              <button
                                onClick={() => handleDeleteUser(u)}
                                style={{ padding: '6px 13px', border: 'none', borderRadius: 8, background: SYS_RED + '12', cursor: 'pointer', color: SYS_RED, fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4, fontFamily: FONT }}
                              >
                                <Trash2 size={11} /> Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── APP SETTINGS ── */}
        {tab === 'app' && (
          <div style={{ maxWidth: 560 }}>
            <div style={{ ...CARD, padding: 28 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: INK, marginTop: 0, marginBottom: 24 }}>Application Settings</h3>

              {settingsSaved && (
                <div style={{ background: SYS_GREEN + '18', borderRadius: 10, padding: '11px 14px', color: SYS_GREEN, fontSize: 13, marginBottom: 20, fontWeight: 500, fontFamily: FONT }}>
                  Settings saved successfully.
                </div>
              )}

              <form onSubmit={handleSaveSettings}>
                <div style={{ marginBottom: 16 }}>
                  <label htmlFor="s-appname" style={LBL}>App Name</label>
                  <input id="s-appname" type="text" value={settings.appName || ''} onChange={e => setSettings(s => ({ ...s, appName: e.target.value }))} style={INP} />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label htmlFor="s-phone" style={LBL}>Business Phone</label>
                  <input id="s-phone" type="tel" value={settings.businessPhone || ''} onChange={e => setSettings(s => ({ ...s, businessPhone: e.target.value }))} style={INP} />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label htmlFor="s-email" style={LBL}>Business Email</label>
                  <input id="s-email" type="email" value={settings.businessEmail || ''} onChange={e => setSettings(s => ({ ...s, businessEmail: e.target.value }))} style={INP} />
                </div>

                <div style={{ marginBottom: 28 }}>
                  <label htmlFor="s-address" style={LBL}>Business Address</label>
                  <textarea id="s-address" value={settings.businessAddress || ''} onChange={e => setSettings(s => ({ ...s, businessAddress: e.target.value }))} rows={2} style={{ ...INP, resize: 'vertical' }} />
                </div>

                <button
                  type="submit"
                  disabled={saving}
                  style={{ padding: '11px 26px', border: 'none', borderRadius: 12, background: BRAND, color: 'white', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 15, fontWeight: 600, opacity: saving ? 0.8 : 1, fontFamily: FONT }}
                >
                  {saving ? 'Saving…' : 'Save Settings'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* ── AUDIT LOG ── */}
        {tab === 'audit' && (
          <div>
            <div style={{ ...CARD, overflow: 'hidden' }}>
              {/* Audit header */}
              <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                <Shield size={16} color={BRAND} />
                <span style={{ fontSize: 15, fontWeight: 700, color: INK }}>Security Audit Log</span>
                <span style={{ fontSize: 12, color: MUTED, marginLeft: 4 }}>({auditLogs.length} entries)</span>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#F7F7F7' }}>
                    {['Timestamp', 'User', 'Action', 'Details', 'IP'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '10px 16px', color: MUTED, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: FONT }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.length === 0 ? (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', padding: 40, color: MUTED, fontFamily: FONT, fontSize: 14 }}>
                        No audit entries.
                      </td>
                    </tr>
                  ) : auditLogs.map((log, idx) => {
                    const isDelete = log.action.includes('DELETE');
                    const isLogin  = log.action.includes('LOGIN');
                    const actionBg    = isDelete ? SYS_RED + '15'    : isLogin ? SYS_GREEN + '15'  : SYS_BLUE + '12';
                    const actionColor = isDelete ? SYS_RED            : isLogin ? SYS_GREEN          : SYS_BLUE;
                    return (
                      <tr key={log.id} style={{ borderTop: idx === 0 ? 'none' : '1px solid rgba(0,0,0,0.05)', background: 'white' }}>
                        <td style={{ padding: '10px 16px', color: MUTED, whiteSpace: 'nowrap', fontSize: 11, fontFamily: FONT }}>
                          {new Date(log.timestamp).toLocaleString()}
                        </td>
                        <td style={{ padding: '10px 16px', color: INK, fontWeight: 500, fontFamily: FONT }}>
                          {log.userName}
                        </td>
                        <td style={{ padding: '10px 16px' }}>
                          <span style={{ padding: '3px 9px', borderRadius: 7, fontSize: 10, fontWeight: 700, background: actionBg, color: actionColor, fontFamily: FONT, letterSpacing: '0.04em' }}>
                            {log.action}
                          </span>
                        </td>
                        <td style={{ padding: '10px 16px', color: MUTED, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: FONT }}>
                          {log.details}
                        </td>
                        <td style={{ padding: '10px 16px', color: MUTED, fontFamily: 'monospace', fontSize: 11 }}>
                          {log.ip}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* User Modal */}
      {userModal && (
        <UserModal
          user={userModal === 'new' ? null : userModal}
          currentUser={currentUser}
          onSave={handleSaveUser}
          onClose={() => setUserModal(null)}
        />
      )}
    </div>
  );
}
